interface RiskSealCollectResult {
  session_id?: string | null;
  error?: string;
}

type RiskSealCommand = 'config' | 'collect';
type RiskSealSDK = (command: RiskSealCommand, ...args: unknown[]) => Promise<RiskSealCollectResult>;

const RISKSEAL_APP_ID = process.env.REACT_APP_RISKSEAL_APP_ID;
const RISKSEAL_SCRIPT_URL = process.env.REACT_APP_RISKSEAL_SCRIPT_URL ?? 'https://cdn.riskseal.io/rs-sdk.js';
const SCRIPT_ID = 'riskseal-sdk-script';

let isConfigured = false;
let cachedSessionId: string | null = null;
let collectPromise: Promise<string | null> | null = null;

const isBrowserEnvironment = (): boolean => {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
};

const loadRiskSealScript = async (): Promise<void> => {
  if (!isBrowserEnvironment()) {
    throw new Error('RiskSeal SDK can be loaded only in browser environment');
  }

  const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existingScript?.dataset.loaded === 'true') {
    return;
  }

  if (existingScript) {
    await new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => {
        existingScript.dataset.loaded = 'true';
        resolve();
      });
      existingScript.addEventListener('error', () => {
        reject(new Error(`Failed to load RiskSeal script from ${RISKSEAL_SCRIPT_URL}`));
      });
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = RISKSEAL_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', () => {
      reject(new Error(`Failed to load RiskSeal script from ${RISKSEAL_SCRIPT_URL}`));
    });
    document.head.appendChild(script);
  });
};

const getRiskSealSdk = (): RiskSealSDK | null => {
  const sdk = window.rsSDK;
  if (typeof sdk !== 'function') {
    return null;
  }
  return sdk as RiskSealSDK;
};

const configureRiskSeal = async (): Promise<RiskSealSDK | null> => {
  if (!RISKSEAL_APP_ID) {
    return null;
  }

  await loadRiskSealScript();
  const sdk = getRiskSealSdk();
  if (!sdk) {
    throw new Error('RiskSeal SDK global is not available');
  }

  if (!isConfigured) {
    const configOptions: Record<string, unknown> = {
      bb: { auto: true, holdSubmitTimeout: 1000 },
    };
    sdk('config', RISKSEAL_APP_ID, configOptions);
    isConfigured = true;
  }

  return sdk;
};

export const collectRiskSealSessionId = async (): Promise<string | null> => {
  if (!RISKSEAL_APP_ID) {
    return null;
  }

  if (cachedSessionId) {
    return cachedSessionId;
  }

  if (collectPromise) {
    return await collectPromise;
  }

  collectPromise = (async () => {
    try {
      const sdk = await configureRiskSeal();
      if (!sdk) {
        return null;
      }

      const result = await sdk('collect');
      const sessionId = typeof result?.session_id === 'string' && result.session_id !== '' ? result.session_id : null;
      cachedSessionId = sessionId;

      return sessionId;
    } catch (error) {
      console.error('RiskSeal collect failed', error);
      return null;
    } finally {
      collectPromise = null;
    }
  })();

  return await collectPromise;
};
